// src/modules/leaves/pages/LeavePage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Tag } from "antd";
import { useTranslation } from "react-i18next";
import PageHeader from "components/Common/PageHeader";
import { all_routes } from "router/all_routes";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique, { FilterField } from "components/Common/TableFilter/TableFilterDynamique";
import Datatable from "components/Common/dataTable";
import UserProfileHeader from "components/Common/ui/UserProfileHeader";
import { formatToLocaleDate } from "utils/dateUtils";
import useLeave from "../hooks/useLeave";
import useReference from "hooks/useReference";
import Loader from "components/Common/loader";
import AddEditLeave from "../components/AddEditLeave";
import LeaveTable from "../components/leaveTable";
import RejectedLeaveModel from "../components/RejectedLeaveModal";
import { Leave } from "types/leaveTypes";



const LeavePage: React.FC = () => {
  const { t } = useTranslation();
  const { leaves, loading, fetchLeaves, infoPagination, setIsModalOpen, isModalOpen, handleCreate, handleUpdate, statusLeaveOptions, handleDelete, handleUpdateStatus } = useLeave();
  const { leaveTypes, loadingLeaveTypes, fetchLeaveTypes } = useReference();
  const [isModalRejectedOpen, setIsModalRejectedOpen] = useState<boolean>(false);

  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});
  const [selectedLeave, setSelectedLeave] = useState<any>(null);
  const breadcrumbItems = [
    { label: "dashboard", path: all_routes.dashboard },
    { label: "leave.title", active: true }, // add "leave.title" in i18n
  ];

  const leaveTypeOptions = useMemo(
    () => (leaveTypes || []).map((lt) => ({ label: lt.name, value: lt.id })),
    [leaveTypes]
  );


  const filterFields: FilterField[] = useMemo(
    () => [
      { key: "search", label: t("search"), type: "text" },
      { key: "status", label: t("leave.form.status"), type: "select", options: statusLeaveOptions },
      { key: "leaveTypeId", label: t("leave.form.leaveType"), type: "select", options: leaveTypeOptions },
      // { key: "dateRange", label: t("date_range"), type: "range" },
    ],
    [t, statusLeaveOptions, leaveTypeOptions, loadingLeaveTypes]
  );



  const handleApproved = async (id: any) => {
    try {
      const payload = {
        status: 'approved' as any
      }
      const result = await handleUpdateStatus(id, payload);
      if (result) {
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
  }

  const handleRejected = async (payload: any) => {
    try {
      const result = await handleUpdateStatus(selectedLeave?.id, payload);
      if (result) {
        setIsModalRejectedOpen(false);
        setSelectedLeave(null);
      }
    } catch (error) {
      console.error("Error fetching leaves:", error);
    }
  }

  const openRejectedModel = (record: any) => {
    setSelectedLeave(record)
    setIsModalRejectedOpen(true)

  }


  const handleFilter = (newFilters: { [key: string]: any }) => {
    const { dateRange, ...rest } = newFilters || {};
    const dateFrom = Array.isArray(dateRange) && dateRange[0] ? dateRange[0] : null;
    const dateTo = Array.isArray(dateRange) && dateRange[1] ? dateRange[1] : null;

    const merged = {
      ...rest,
      dateFrom,
      dateTo,
    };

    setFilters(merged);
    fetchLeaves({
      ...merged,
      page: 1,
      limit: 10,
    });
  };

  useEffect(() => {
    fetchLeaveTypes();
    fetchLeaves({
      page: 1,
      limit: 10,
    });
  }, []);



  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader title={t("leave.title")} breadcrumbItems={breadcrumbItems} />

        <div className="d-flex mb-3 justify-content-end gap-2">
          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilter}
            onReset={() => handleFilter({ page: 1, limit: 10 })}
          />
        </div>

        <LeaveTable
          dataSource={leaves}
          loading={loading}
          onApprove={(record: any) => {
            handleApproved(record.id)
          }}
          onRejected={openRejectedModel}
          dataPaginate={{
            current: infoPagination?.page,
            pageSize: infoPagination?.limit,
            total: infoPagination?.total,
          }}
          onChange={(pg) => {
            fetchLeaves({
              ...filters,
              page: pg.current!,
              limit: pg.pageSize!,
            });
          }}
        />

        <RejectedLeaveModel
          isOpen={isModalRejectedOpen}
          onClose={() => setIsModalRejectedOpen(false)}
          leaveData={selectedLeave}
          onSave={(value) => handleRejected(value)}
        />
      </div>
    </div>
  );
};

export default LeavePage;
